/* eslint-disable react/prop-types */
import React, { useMemo, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Field, formValueSelector } from 'redux-form'
import { lpForm } from 'lp-form'
import {
  ReadOnlyCellInput,
  NestedAttributesFieldArray,
  SectionHeader,
} from 'components'
import { noop, groupBy, size, get } from 'lodash'
import * as Types from 'types'
import { pluralize } from 'utils'
import {
  AddArtistPartnershipModal,
  CopyArtistPartnershipModal,
} from '../components'
import * as flashActions from 'redux-flash'
import { SUCCESS_FLASH_MODAL_CLOSE_DELAY } from 'config'
import classnames from 'classnames'
import { ArrowUpIcon, ArrowDownIcon } from 'components/icons'
import LoadingSpinner from 'components/loading-spinner'
import LoadingContainer from 'components/loading-container'

const propTypes = {
  change: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  flashSuccessMessage: PropTypes.func.isRequired,
  previousPartnerships: PropTypes.arrayOf(Types.partnership),
  artistOptions: PropTypes.arrayOf(Types.searchSuggestion),
  partnerships: PropTypes.arrayOf(Types.partnerPartnershipType),
  schoolYears: PropTypes.arrayOf(Types.schoolYear).isRequired,
  submitting: PropTypes.bool.isRequired,
  submitFailed: PropTypes.bool.isRequired,
  visibleSchoolYearId: PropTypes.number.isRequired,
}

const defaultProps = {
  error: '',
  partnerships: [],
  disabled: false,
  previousPartnerships: [],
}

function useActiveArtistCallback(artistOptions) {
  const activeArtistMap = useMemo(
    () => groupBy(artistOptions, 'id'),
    [artistOptions]
  )
  return useCallback(
    (artistId) => {
      return !!activeArtistMap[artistId]
    },
    [activeArtistMap]
  )
}

function ArtistRow({
  fields,
  index,
  handleSubmit,
  setLoading,
  change,
  partnerships,
}) {
  const [showPrograms, setShowPrograms] = useState(false)
  const field = fields.name ? `${fields.name}[${index}]` : `${index}`
  const artist = fields.get(index)

  const handleAccept = () => {
    setLoading(true)
    try {
      // Update the local form state to reflect the change
      const updatedArtist = { ...artist, state: 'approved' }

      // Create a new partnerships array with the updated artist
      const updatedPartnerships = [...partnerships]
      updatedPartnerships[index] = updatedArtist
      change('partnerships', updatedPartnerships)

      // Submit the form to persist changes
      setTimeout(() => {
        handleSubmit()
        setLoading(false)
      }, 0)
    } catch (error) {
      setLoading(false)
    }
  }

  const handleDecline = () => {
    setLoading(true)
    try {
      fields.destroy(index)

      // Submit the form to persist changes
      setTimeout(() => {
        handleSubmit()
        setLoading(false)
      }, 0)
    } catch (error) {
      setLoading(false)
    }
  }

  return (
    <React.Fragment>
      <tr role="group" aria-label={`Teaching artist ${artist.artistName}`}>
        <th scope="row" className="table-name">
          <div className="table-name-wrapper">
            <button
              type="button"
              className="remove-row action-button"
              onClick={handleDecline}
              aria-label={`Decline and remove ${
                artist.artistName || 'teaching artist'
              } partnership`}
            >
              <span aria-hidden="true">×</span>
            </button>
            <Field
              name={field + '.artistName'}
              label={false}
              component={ReadOnlyCellInput}
            />
            {artist.state === 'pending_approval' && (
              <div className="pending-approval">Pending</div>
            )}
          </div>
        </th>
        <td className="table-email">
          <Field
            name={field + '.artistEmail'}
            label={false}
            component={ReadOnlyCellInput}
          />
        </td>
        <td className="table-actions">
          <div className="table-actions-wrapper">
            {artist.state !== 'rejected' && (
              <button
                type="button"
                className="button-primary-outline button-small"
                onClick={handleDecline}
              >
                Decline
              </button>
            )}
            {artist.state !== 'approved' && (
              <button
                type="button"
                className="button-primary button-small"
                onClick={handleAccept}
              >
                Accept
              </button>
            )}
            {artist.partnerPrograms?.length > 0 && (
              <>
                <button
                  type="button"
                  className={classnames('action-button', {
                    'visually-hidden': !showPrograms,
                  })}
                  onClick={() => setShowPrograms(false)}
                  aria-label="Hide program list"
                >
                  <ArrowUpIcon />
                </button>
                <button
                  type="button"
                  className={classnames('action-button', {
                    'visually-hidden': showPrograms,
                  })}
                  onClick={() => setShowPrograms(true)}
                  aria-label="Show program list"
                >
                  <ArrowDownIcon />
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
      <tr className="table-programs">
        <td colSpan="3">
          <ul className={classnames({ 'visually-hidden': !showPrograms })}>
            {artist.partnerPrograms?.map((program, programIndex) => (
              <li
                key={`artist-${index}-program-${programIndex}-${
                  program.programId || program.name
                }`}
              >
                {program.name}
              </li>
            ))}
          </ul>
        </td>
      </tr>
    </React.Fragment>
  )
}

function ArtistFields({
  fields,
  handleSubmit,
  setLoading,
  change,
  partnerships,
}) {
  return (
    <tbody>
      {fields.map((_, i) => {
        return (
          <ArtistRow
            key={`artist-${i}`}
            index={i}
            fields={fields}
            handleSubmit={handleSubmit}
            setLoading={setLoading}
            change={change}
            partnerships={partnerships}
          />
        )
      })}
    </tbody>
  )
}

function ArtistPartnershipTableForm({
  change,
  disabled,
  error,
  flashSuccessMessage,
  handleSubmit,
  schoolYears,
  visibleSchoolYearId,
  partnerships,
  artistOptions,
  previousPartnerships,
  submitFailed,
  updateArtist,
}) {
  const isActiveArtist = useActiveArtistCallback(artistOptions)
  const [loading, setLoading] = useState(false)

  const visibleYear = useMemo(() => {
    return schoolYears.find(({ id }) => id === visibleSchoolYearId)
  }, [schoolYears, visibleSchoolYearId])

  // Determine what partnerships exist with _active_ organizations that have not already been added for the current year
  const remainingPreviousPartnerships = useMemo(() => {
    const eligiblePartnerships = previousPartnerships.filter((partnership) => {
      return (
        isActiveArtist(partnership.teachingArtistId) &&
        !partnerships.some(
          ({ teachingArtistId }) =>
            teachingArtistId === partnership.teachingArtistId
        )
      )
    })

    return eligiblePartnerships
  }, [previousPartnerships.length, partnerships.length, isActiveArtist])

  const remainingArtistOptions = useMemo(() => {
    return artistOptions.filter((artist) => {
      return !partnerships.some(
        ({ teachingArtistId }) => teachingArtistId === artist.id
      )
    })
  }, [artistOptions, partnerships])

  const [showAddPartnershipModal, setShowAddPartnershipModal] = useState(false)
  const [showCopyPartnershipModal, setShowCopyPartnershipModal] =
    useState(false)

  return (
    <SectionHeader>
      <h2>What teaching artists did you partner with?</h2>
      <p>
        Please list all the teaching artists and the type(s) of program(s) you
        work with.
      </p>
      {!disabled && (
        <div className="button-area many-buttons">
          <button
            type="button"
            onClick={() => setShowAddPartnershipModal(true)}
            className="button-primary-outline"
          >
            Add a new teaching artist
          </button>
          <button
            type="button"
            onClick={() => setShowCopyPartnershipModal(true)}
            className="button-primary-outline"
          >
            Copy teaching artists from other school years
          </button>
        </div>
      )}

      <div className="portal partner-artists">
        <LoadingContainer isLoading={loading}>
          {loading && <LoadingSpinner />}
          {partnerships && partnerships.length > 0 ? (
            <form onSubmit={disabled ? noop : handleSubmit} disabled={disabled}>
              <table>
                <thead>
                  <tr>
                    <th scope="col">Teaching Artist Name</th>
                    <th scope="col">Email</th>
                    <th scope="col" aria-label="Actions"></th>
                  </tr>
                </thead>
                <NestedAttributesFieldArray
                  name="partnerships"
                  component={ArtistFields}
                  handleSubmit={handleSubmit}
                  setLoading={setLoading}
                  disabled={disabled}
                  change={change}
                  partnerships={partnerships}
                />
              </table>
              {submitFailed && error && (
                <div className="partnership-table-errors">
                  <p className="error-message">{error}</p>
                </div>
              )}
            </form>
          ) : (
            <p>
              No artists reported for the{' '}
              {get(visibleYear, 'number', 'selected')} school year.
            </p>
          )}
        </LoadingContainer>
      </div>
      {showAddPartnershipModal && (
        <AddArtistPartnershipModal
          onClose={() => setShowAddPartnershipModal(false)}
          onSubmit={async ({ artist, programs, schoolIds }) => {
            setLoading(true)

            // If schoolIds are provided, create school-specific partnerships using updateArtist
            if (schoolIds && schoolIds.length > 0) {
              try {
                // Create partnerships for each selected school
                const schoolPartnerships = schoolIds.map((schoolId) => ({
                  schoolId: schoolId,
                }))

                // Update the artist with the new school partnerships
                await updateArtist(artist.id, visibleSchoolYearId, {
                  partnerships: schoolPartnerships,
                })
              } catch (error) {
                setLoading(false)
                // Re-throw the error so it can be handled by the form's error system
                throw new Error(
                  `Failed to create school partnerships: ${
                    error.message || 'Unknown error'
                  }`
                )
              }
            }

            // Create the organization partnership (existing behavior)
            const newPartnership = {
              teachingArtistId: artist.id,
              state: 'approved', // Always set approved state for new partnerships when organization adds artist
              artistName: artist.name,
              artistEmail: artist.email,
              partnerPrograms: programs.map((program) => {
                return {
                  programId: program.id,
                  name: program.name,
                }
              }),
            }
            change('partnerships', [...partnerships, newPartnership])

            // Submit the form after a brief delay to ensure state is updated
            setTimeout(() => {
              handleSubmit()
              setLoading(false)
            }, 0)
            return {
              ...newPartnership,
              hasSchoolPartnerships: schoolIds && schoolIds.length > 0,
            }
          }}
          onSubmitSuccess={({ artistName, hasSchoolPartnerships }) => {
            setShowAddPartnershipModal(false)
            const schoolPartnershipMessage = hasSchoolPartnerships
              ? ` and school partnerships created`
              : ''
            setTimeout(
              () =>
                flashSuccessMessage(
                  `Partnership with ${artistName} added for the ${visibleYear.number} school year${schoolPartnershipMessage}`
                ),
              SUCCESS_FLASH_MODAL_CLOSE_DELAY
            )
          }}
          artistOptions={remainingArtistOptions}
          visibleSchoolYearId={visibleSchoolYearId}
        />
      )}

      {showCopyPartnershipModal && (
        <CopyArtistPartnershipModal
          onClose={() => setShowCopyPartnershipModal(false)}
          onSubmit={async ({ partnerships: newPartnerships }) => {
            setLoading(true)
            change('partnerships', [...partnerships, ...newPartnerships])
            // Submit the form after a brief delay to ensure state is updated
            setTimeout(() => {
              handleSubmit()
              setLoading(false)
            }, 0)
            return newPartnerships
          }}
          onSubmitSuccess={(partnerships) => {
            setShowCopyPartnershipModal(false)
            const numNewPartnerships = size(partnerships)
            setTimeout(
              () =>
                flashSuccessMessage(
                  `${numNewPartnerships} ${pluralize(
                    'partnership',
                    numNewPartnerships
                  )} copied over for the ${visibleYear.number} school year`
                ),
              SUCCESS_FLASH_MODAL_CLOSE_DELAY
            )
          }}
          partnerships={remainingPreviousPartnerships}
          schoolYears={schoolYears}
        />
      )}
    </SectionHeader>
  )
}

ArtistPartnershipTableForm.propTypes = propTypes
ArtistPartnershipTableForm.defaultProps = defaultProps

function mapStateToProps(state, { name }) {
  return {
    partnerships: formValueSelector(name)(state, 'partnerships'),
  }
}

const mapDispatchToProps = {
  flashSuccessMessage: flashActions.flashSuccessMessage,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  lpForm({
    submitFilters: {
      allow: ['id', 'partnerships'],
    },
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    destroyOnUnmount: false, // allow switching between year tabs without losing in-progress data
  })
)(ArtistPartnershipTableForm)
